import { useQuery } from "@tanstack/react-query";
import { Link, useLocation } from "react-router-dom";
import { apiClient, responseData } from "@/api/client";
import type { CardBrandOverview, CardBrandOverviewItem } from "@/api/types";

export type CardOverviewStage = "load" | "validate" | "reconcile" | "review";

export function useCardBrandOverview(
  hotelId: string,
  period: string,
  cardBrandId: string,
  enabled = true,
) {
  return useQuery({
    queryKey: ["card-brand-overview", hotelId, period, cardBrandId],
    queryFn: (): Promise<CardBrandOverview> => responseData(apiClient.GET("/api/v1/cards/brand-overview", {
      params: { query: { hotelId, period, cardBrandId: cardBrandId || undefined } },
    })),
    enabled: enabled && Boolean(hotelId),
  });
}

const stageStateLabel = (state: CardBrandOverviewItem["stages"][CardOverviewStage]) => {
  if (state === "COMPLETE") return "Completa";
  if (state === "PARTIAL") return "Parcial";
  if (state === "ACTION_REQUIRED") return "Requiere atención";
  return "Sin iniciar";
};

const missingLoadLabel = (brand: CardBrandOverviewItem) => {
  const missing = [
    brand.loads.liquidationStatus === null ? "Liquidación" : "",
    brand.loads.ledgerStatus === null ? "Libro Mayor" : "",
  ].filter(Boolean);
  return missing.length ? `Falta ${missing.join(" y ")}` : "Sin cargas confirmadas";
};

function StageMark({ state }: { state: CardBrandOverviewItem["stages"][CardOverviewStage] }) {
  return <span className={`bank-account-stage-mark bank-account-stage-mark--${state.toLowerCase()}`} aria-label={stageStateLabel(state)}>
    {state === "COMPLETE" ? "✓" : state === "ACTION_REQUIRED" ? "!" : state === "PARTIAL" ? "◐" : "—"}
  </span>;
}

function BrandLine({ brand, activeStage, href }: { brand: CardBrandOverviewItem; activeStage: CardOverviewStage; href: string }) {
  const reconciledCount = brand.reconciliation.auto + brand.reconciliation.approved + brand.reconciliation.adjusted;
  const loadDetail = brand.loads.activeCount
    ? `${brand.loads.activeCount} carga${brand.loads.activeCount === 1 ? "" : "s"} en proceso`
    : brand.loads.previewCount
      ? `${brand.loads.previewCount} por confirmar`
      : brand.loads.errorCount
        ? `${brand.loads.errorCount} error${brand.loads.errorCount === 1 ? "" : "es"}`
        : brand.stages.load === "COMPLETE"
          ? "Liquidación y Mayor confirmados"
          : missingLoadLabel(brand);
  const stageClass = (stage: CardOverviewStage) => stage === activeStage ? " is-active" : "";
  return <Link className="bank-account-line" to={href} aria-label={`Abrir ${brand.brandName}`}>
    <header className="bank-account-line__identity">
      <h3>{brand.brandName}</h3>
      {!brand.active && <span className="status status--warning">Histórica</span>}
    </header>
    <div className={`bank-account-line__stage${stageClass("load")}`}>
      <StageMark state={brand.stages.load} />
      <span><small>Carga</small><strong>{loadDetail}</strong><em>{brand.loads.liquidationRecords} liquidación · {brand.loads.ledgerRecords} Mayor</em></span>
    </div>
    <div className={`bank-account-line__stage${stageClass("validate")}`}>
      <StageMark state={brand.stages.validate} />
      <span><small>Validación</small><strong>{brand.movements.settlements + brand.movements.ledger} registros</strong><em>{brand.movements.settlements} cupones · {brand.movements.ledger} asientos</em></span>
    </div>
    <div className={`bank-account-line__stage${stageClass("reconcile")}`}>
      <StageMark state={brand.stages.reconcile} />
      <span><small>Conciliación</small><strong>{brand.reconciliation.processed ? `${reconciledCount} conciliadas` : brand.stages.validate === "COMPLETE" ? "Lista para conciliar" : "Bloqueada"}</strong><em>{brand.reconciliation.suggested} sugeridas · {brand.reconciliation.unmatchedOrigin + brand.reconciliation.unmatchedCounterpart} sin coincidencia</em></span>
    </div>
    <div className={`bank-account-line__stage${stageClass("review")}`}>
      <StageMark state={brand.stages.review} />
      <span><small>Arrastre y revisión</small><strong>{brand.reconciliation.priorUnmatched} pendientes arrastrados</strong><em>{brand.reconciliation.observationCount} observaciones</em></span>
    </div>
    <span className="bank-account-line__open" aria-hidden="true">→</span>
  </Link>;
}

export function CardBrandsOverview({ data, activeStage }: { data: CardBrandOverview; activeStage: CardOverviewStage }) {
  const location = useLocation();
  const brandHref = (brand: CardBrandOverviewItem) => {
    const query = new URLSearchParams({ masterId: brand.brandId });
    return `${location.pathname}?${query}`;
  };
  return <section className="bank-accounts-overview" aria-label="Marcas del hotel">
    <div className="bank-account-lines" aria-label="Estado por marca de tarjeta">
      {data.brands.map((brand) => <BrandLine key={brand.brandId} brand={brand} activeStage={activeStage} href={brandHref(brand)} />)}
    </div>
    {!data.brands.length && <div className="bank-accounts-overview__empty"><strong>No hay marcas para este alcance.</strong><span>Configurá una marca o cambiá los filtros del período.</span></div>}
  </section>;
}
